import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import i18n from 'i18next';
import { QAWAEM_LOGO_FULL } from '../../products/saudi/config/qawaemAssets';
import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import '../../products/saudi/layout/saudi-qawaem.css';

const t = (key, opts) => i18n.t(`error_boundary.${key}`, opts);

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught error:', error);
    console.error('Error info:', errorInfo);

    this.setState((prev) => ({
      error,
      errorInfo,
      errorCount: prev.errorCount + 1,
    }));
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/#/';
  };

  handleReload = () => {
    window.location.reload();
  };

  renderQawaemFallback() {
    const { error, errorInfo, errorCount } = this.state;
    const isRtl = i18n.dir?.() === 'rtl' || i18n.language?.startsWith('ar');

    return (
      <div
        className="qawaem-preauth qawaem-error-screen min-h-screen flex items-center justify-center px-4 py-10"
        dir={isRtl ? 'rtl' : 'ltr'}
        data-brand="qawaem"
      >
        <div className="qawaem-error-card glass-modal w-full max-w-lg p-8 text-center">
          <div className="flex flex-col items-center gap-3 mb-6">
            <img
              src={QAWAEM_LOGO_FULL}
              alt={SAUDI_BRAND.appName}
              className="h-12 w-auto object-contain"
            />
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-red-200/80 bg-red-50">
              <AlertTriangle className="h-7 w-7 text-red-600" strokeWidth={2.2} aria-hidden />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-console">{t('title')}</h1>
          <p className="mt-3 text-sm leading-relaxed text-console-muted">{t('description')}</p>

          {process.env.NODE_ENV === 'development' && error && (
            <div className="qawaem-error-details mt-6 max-h-48 overflow-auto rounded-xl border border-red-200/70 bg-red-50/90 p-4 text-start">
              <p className="mb-2 font-mono text-xs text-red-800">
                <strong>{t('dev_error_label')}:</strong> {error.toString()}
              </p>
              {errorInfo?.componentStack ? (
                <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-red-700/90">
                  {errorInfo.componentStack}
                </pre>
              ) : null}
            </div>
          )}

          {errorCount > 2 && (
            <div className="mt-4 rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {t('repeat_warning', { count: errorCount })}
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={this.handleReset}
              className="qawaem-error-btn qawaem-error-btn--primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-all"
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              {t('retry')}
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="qawaem-error-btn qawaem-error-btn--secondary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-all"
            >
              <Home className="h-4 w-4" aria-hidden />
              {t('home')}
            </button>
            {errorCount > 1 && (
              <button
                type="button"
                onClick={this.handleReload}
                className="qawaem-error-btn qawaem-error-btn--ghost inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-all"
              >
                <RefreshCw className="h-4 w-4" aria-hidden />
                {t('reload')}
              </button>
            )}
          </div>

          <p className="mt-8 border-t border-[#6e9b6e]/15 pt-5 text-xs text-console-subtle">
            {t('support')}
          </p>
        </div>
      </div>
    );
  }

  renderLegacyFallback() {
    const { error, errorInfo, errorCount } = this.state;

    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl max-w-2xl w-full p-8">
          <div className="flex justify-center mb-6">
            <div className="bg-red-100 p-4 rounded-full">
              <AlertTriangle className="w-12 h-12 text-red-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-4">{t('title')}</h1>
          <p className="text-slate-600 text-center mb-6">{t('description')}</p>
          {process.env.NODE_ENV === 'development' && error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 max-h-64 overflow-auto text-start">
              <p className="text-red-800 font-mono text-sm mb-2">
                <strong>{t('dev_error_label')}:</strong> {error.toString()}
              </p>
              {errorInfo?.componentStack ? (
                <pre className="text-red-700 font-mono text-xs whitespace-pre-wrap">
                  {errorInfo.componentStack}
                </pre>
              ) : null}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium"
            >
              {t('retry')}
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="px-6 py-3 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-medium"
            >
              {t('home')}
            </button>
            {errorCount > 1 && (
              <button
                type="button"
                onClick={this.handleReload}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium"
              >
                {t('reload')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return isSaudiBrand() ? this.renderQawaemFallback() : this.renderLegacyFallback();
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
